/** Shared Koneko A list sort modes (localStorage-persisted in views). */
export const KONEKO_SORT_NAME = "name";
export const KONEKO_SORT_TIME = "timeSpent";
export const KONEKO_SORT_BUDGET_DELTA = "budgetDelta";

export const STORAGE_MEMBER_BREAKDOWN_SORT = "konekoA:memberBreakdownSortBy";
export const STORAGE_CROSS_COMPARISON_SORT = "konekoA:crossComparisonSortBy";
export const STORAGE_SNAPSHOT_SORT = "konekoA:projectSnapshotSortBy";
export const STORAGE_BUDGET_EDITOR_SORT = "konekoA:budgetEditorSortBy";

export function localeNameSort(a, b) {
  return String(a || "").localeCompare(String(b || ""), undefined, {
    sensitivity: "base",
  });
}

export function readKonekoSortPref(storageKey, allowed, defaultValue) {
  try {
    const v = localStorage.getItem(storageKey);
    if (allowed.includes(v)) return v;
  } catch {
    /* ignore */
  }
  return defaultValue;
}

export function writeKonekoSortPref(storageKey, value) {
  try {
    localStorage.setItem(storageKey, value);
  } catch {
    /* ignore */
  }
}

/** Hours on a member breakdown allocation line. */
export function allocationMonthDec(line) {
  const raw =
    line && (line.monthLogged != null ? line.monthLogged : line.month);
  if (raw == null || raw === "") return 0;
  const s = String(raw).trim();
  if (!s) return 0;
  if (s.includes(":")) {
    const parts = s.split(":");
    const h = parseInt(parts[0], 10);
    const m = parts.length > 1 ? parseInt(parts[1], 10) : 0;
    const hh = Number.isFinite(h) ? h : 0;
    const mm = Number.isFinite(m) ? m : 0;
    return hh + mm / 60;
  }
  const n = parseFloat(s.replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

export function memberMonthTotalDec(member) {
  const lines =
    member && Array.isArray(member.allocations) ? member.allocations : [];
  return lines.reduce((sum, line) => sum + allocationMonthDec(line), 0);
}

export function sortMemberAllocations(allocations, sortBy, hoursToDecimalFn) {
  const list = Array.isArray(allocations) ? [...allocations] : [];
  if (sortBy === KONEKO_SORT_TIME) {
    const dec = hoursToDecimalFn || allocationMonthDec;
    return list.sort(
      (a, b) =>
        dec(b.monthLogged) - dec(a.monthLogged) ||
        localeNameSort(a.project, b.project)
    );
  }
  return list.sort((a, b) => localeNameSort(a.project, b.project));
}

export function sortVisibleMembers(members, sortBy, hoursToDecimalFn) {
  const list = Array.isArray(members) ? [...members] : [];
  const withAlloc = list.map((m) => ({
    ...m,
    allocations: sortMemberAllocations(m.allocations, sortBy, hoursToDecimalFn),
  }));
  if (sortBy === KONEKO_SORT_TIME) {
    return withAlloc.sort(
      (a, b) =>
        memberMonthTotalDec(b) - memberMonthTotalDec(a) ||
        localeNameSort(a.name || a.username, b.name || b.username)
    );
  }
  return withAlloc.sort((a, b) =>
    localeNameSort(a.name || a.username, b.name || b.username)
  );
}

/** % of selected cap used (0 when FLEX / no cap). */
export function snapshotBudgetUsageRatio(row) {
  const bar = row && row.budgetBar;
  if (!bar) return 0;
  const current = Number(bar.current);
  if (!Number.isFinite(current) || current < 0) return 0;
  const d = row.snapshotBudgetDeltaHours;
  if (d == null || !Number.isFinite(d)) return 0;
  const denom = current - d;
  if (!Number.isFinite(denom) || denom <= 0) return 0;
  return current / denom;
}

/**
 * Sort rank for snapshot “Over budget (Δ)”:
 * red (over cap) → amber (ahead of pace) → on-track by % used (lowest last) → FLEX.
 */
export function snapshotBudgetSortRank(row) {
  if (!row) return {tier: -1, score: 0};
  if (row.snapshotOverBudget) {
    const d = row.snapshotBudgetDeltaHours;
    const score = d != null && Number.isFinite(d) && d > 0 ? d : 0.001;
    return {tier: 3, score};
  }
  if (row.snapshotPastCalendarPaceLine) {
    const p = row.snapshotPaceAheadHours;
    const score = p != null && Number.isFinite(p) && p > 0 ? p : 0.001;
    return {tier: 2, score};
  }
  const d = row.snapshotBudgetDeltaHours;
  if (d == null) return {tier: 0, score: 0};
  return {tier: 1, score: snapshotBudgetUsageRatio(row)};
}

/** Project snapshot rows: warnings first, then by magnitude (FLEX last). */
export function compareSnapshotBudgetDelta(a, b) {
  const ra = snapshotBudgetSortRank(a);
  const rb = snapshotBudgetSortRank(b);
  if (rb.tier !== ra.tier) return rb.tier - ra.tier;
  if (rb.score !== ra.score) return rb.score - ra.score;
  return localeNameSort(a && (a.name || a.project), b && (b.name || b.project));
}

/** Budget Editor rows (`project` key) — same warning-first order as snapshot. */
export function sortBudgetEditorRows(rows, sortBy) {
  const list = Array.isArray(rows) ? rows : [];
  const normalized = list.map((r) => ({...r, name: r.name || r.project}));
  return sortSnapshotProjectRows(normalized, sortBy);
}

/** Cross comparison: estimate vs org max (no max → last). */
export function compareCrossComparisonBudgetDelta(a, b) {
  const da = a && a._sortBudgetDelta;
  const db = b && b._sortBudgetDelta;
  if (da == null && db == null) {
    return localeNameSort(a && a.project, b && b.project);
  }
  if (da == null) return 1;
  if (db == null) return -1;
  if (db !== da) return db - da;
  return localeNameSort(a && a.project, b && b.project);
}

export function sortCrossComparisonRows(rows, sortBy) {
  const list = Array.isArray(rows) ? [...rows] : [];
  if (sortBy === KONEKO_SORT_BUDGET_DELTA) {
    return list.sort(compareCrossComparisonBudgetDelta);
  }
  return list.sort((a, b) => localeNameSort(a.project, b.project));
}

export function sortSnapshotProjectRows(rows, sortBy) {
  const list = Array.isArray(rows) ? [...rows] : [];
  if (sortBy === KONEKO_SORT_BUDGET_DELTA) {
    return list.sort(compareSnapshotBudgetDelta);
  }
  return list.sort((a, b) => localeNameSort(a.name, b.name));
}
